/* morningKiwi — carrito lateral.
 *
 * Todo por delegación en `document`: el contenido del panel se sustituye entero
 * en cada cambio, así que un listener colgado de un botón concreto moriría con
 * el primer repintado.
 *
 * Las mutaciones piden a Shopify la sección ya renderizada (`sections`), en vez
 * de recomponer el HTML aquí: el precio, el descuento del plan y el subtotal los
 * calcula el mismo Liquid que pinta la página, y así no hay dos versiones de la
 * misma cuenta que puedan discrepar.
 */
(function () {
  const SECCION = 'mk-carrito';

  const raiz = () => document.querySelector('[data-mk-carrito]');
  let devolverFoco = null;

  /* El panel NO bloquea el scroll del fondo, ni con el `scroll-lock` de Horizon
     ni con uno propio. Los dos hacen lo mismo —`overflow: hidden` en el elemento
     que se desplaza— y por encima de 990px ese elemento es `.page-wrapper`, que
     ahí mide `100dvh` y es el contenedor de scroll de la página entera. Tocarlo
     dejaba la pantalla en verde: el verde es el fondo del lienzo, o sea, la
     página sin nada pintado encima. Por debajo de 990px el que se desplaza es el
     documento y no pasaba nada, que es por lo que solo se veía en el ordenador.

     A cambio, la lista del panel lleva `overscroll-behavior: contain`, así que
     al llegar a su final la rueda no arrastra la página de detrás. Queda que se
     pueda desplazar el fondo apuntando fuera del panel; es un defecto pequeño al
     lado de perder la página. */

  function abrir() {
    const r = raiz();
    if (!r || r.hasAttribute('data-abierto')) return;
    devolverFoco = document.activeElement;
    r.setAttribute('data-abierto', '');
    const x = r.querySelector('.mk-carrito__x');
    // `preventScroll` porque enfocar arrastra a la vista todos los contenedores
    // de scroll por encima del elemento, y el panel está fijo: no hay nada que
    // traer a la vista, solo movimiento que nadie ha pedido.
    if (x) x.focus({ preventScroll: true });
  }

  function cerrar() {
    const r = raiz();
    if (!r || !r.hasAttribute('data-abierto')) return;
    r.removeAttribute('data-abierto');
    if (devolverFoco && devolverFoco.isConnected) devolverFoco.focus({ preventScroll: true });
    devolverFoco = null;
  }

  function pintar(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nuevo = doc.querySelector('[data-mk-cuerpo]');
    const actual = document.querySelector('[data-mk-cuerpo]');
    if (nuevo && actual) actual.replaceWith(nuevo);

    const fuente = doc.querySelector('[data-mk-carrito]');
    const cuenta = fuente ? fuente.getAttribute('data-mk-count') || '0' : '0';
    const r = raiz();
    if (r) r.setAttribute('data-mk-count', cuenta);
    document.querySelectorAll('[data-mk-cuenta]').forEach((marca) => {
      marca.textContent = cuenta;
      marca.toggleAttribute('hidden', cuenta === '0');
    });
  }

  /* `pintar` en la última llamada de una cadena y no en todas: una conversión a
     suscripción son dos peticiones, y repintar la de en medio enseñaría por un
     instante un carrito con la línea duplicada. */
  async function pedir(url, cuerpo, repintar = true) {
    const respuesta = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({ sections: SECCION }, cuerpo)),
    });
    const datos = await respuesta.json().catch(() => null);
    if (!respuesta.ok) {
      throw new Error((datos && (datos.description || datos.message)) || 'cart');
    }
    if (repintar && datos && datos.sections && datos.sections[SECCION]) {
      pintar(datos.sections[SECCION]);
    }
    return datos;
  }

  function ocupado(estado) {
    const r = raiz();
    if (r) r.toggleAttribute('data-ocupado', estado);
  }

  async function conBloqueo(trabajo) {
    ocupado(true);
    try {
      await trabajo();
    } catch (e) {
      // El fallo se cuenta donde el usuario está mirando, y para eso el panel
      // tiene que estar abierto: si no, un producto agotado o una sesión
      // caducada dejan el botón sin efecto y sin explicación.
      const aviso = document.querySelector('[data-mk-aviso]');
      if (aviso) {
        aviso.textContent = e.message;
        abrir();
      } else {
        window.location.href = '/cart';
      }
    } finally {
      ocupado(false);
    }
  }

  /* El formulario de compra se reconoce por su acción y no por un `id` nuestro:
     el `id` lo pone el tag `form` de Shopify y no está garantizado que respete
     el que le pasamos. La acción sí: cualquier formulario de producto apunta a
     /cart/add. */
  const esFormularioDeCompra = (nodo) =>
    nodo instanceof HTMLFormElement && (nodo.getAttribute('action') || '').indexOf('/cart/add') !== -1;

  function anadir(form) {
    const datos = new FormData(form);
    const cuerpo = { id: datos.get('id'), quantity: 1 };
    const plan = datos.get('selling_plan');
    // El radio de compra única lleva `value=""`: mandarlo vacío haría que
    // Shopify rechazara la petición entera.
    if (plan) cuerpo.selling_plan = Number(plan);

    const boton = form.querySelector('[name="add"]');
    if (boton) boton.disabled = true;

    return conBloqueo(async () => {
      await pedir('/cart/add.js', cuerpo);
      abrir();
    }).finally(() => {
      if (boton) boton.disabled = false;
    });
  }

  /* Dos puertas al mismo sitio. `submit` cubre el envío con el teclado, pero no
     salta si algo llama a `form.submit()` por su cuenta —ese método se salta los
     listeners—, y entonces la página se iría a /cart. El clic en el botón cubre
     ese caso, que es el normal con ratón y con dedo. La que llegue primera corta
     el envío nativo, así que nunca se disparan las dos. */
  document.addEventListener('submit', (evento) => {
    if (!esFormularioDeCompra(evento.target)) return;
    evento.preventDefault();
    anadir(evento.target);
  });

  document.addEventListener('click', (evento) => {
    const objetivo = evento.target;

    const botonAnadir = objetivo.closest('[name="add"]');
    if (botonAnadir) {
      // `form` es la propiedad del control, que también acierta cuando el botón
      // vive fuera del formulario y lo referencia por atributo.
      const form = botonAnadir.form || botonAnadir.closest('form');
      if (esFormularioDeCompra(form)) {
        evento.preventDefault();
        anadir(form);
        return;
      }
    }

    if (objetivo.closest('[data-mk-abrir]')) {
      evento.preventDefault();
      abrir();
      return;
    }

    if (objetivo.closest('[data-mk-cerrar]')) {
      evento.preventDefault();
      cerrar();
      return;
    }

    const cambio = objetivo.closest('[data-mk-clave][data-mk-cantidad]');
    if (cambio) {
      evento.preventDefault();
      conBloqueo(() =>
        pedir('/cart/change.js', {
          id: cambio.dataset.mkClave,
          quantity: Number(cambio.dataset.mkCantidad),
        })
      );
      return;
    }

    const autoship = objetivo.closest('[data-mk-autoship]');
    if (autoship) {
      evento.preventDefault();
      /* Añadir primero y retirar después. `change.js` no cambia el plan de una
         línea existente, así que hay que rehacerla; en este orden, si el alta
         falla el carrito se queda como estaba en vez de vaciarse.

         Se retira por `key` y no por índice de línea: al añadir, la línea nueva
         puede colocarse en cualquier posición y el índice ya no señalaría lo
         mismo. */
      conBloqueo(async () => {
        await pedir(
          '/cart/add.js',
          {
            id: Number(autoship.dataset.mkVariante),
            quantity: Number(autoship.dataset.mkUnidades),
            selling_plan: Number(autoship.dataset.mkPlan),
          },
          false
        );
        await pedir('/cart/change.js', { id: autoship.dataset.mkClave, quantity: 0 });
      });
    }
  });

  document.addEventListener('keydown', (evento) => {
    if (evento.key === 'Escape') cerrar();
  });
})();
